import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Search, ChevronLeft, Phone, Mail, Calendar, Receipt } from 'lucide-react';
import { toast } from 'sonner';

function StatusPill({ status }) {
  const colors = {
    active: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    cancelled: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-sans px-2 py-0.5 rounded-sm border ${colors[status] || colors.cancelled}`}>
      {status}
    </span>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);

  const fetchCustomers = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/customers', { params: q ? { q } : {} });
      setCustomers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const openCustomer = async (c) => {
    setSelected(c);
    setHistory(null);
    try {
      const { data } = await api.get(`/admin/customers/${c.id}/orders`);
      setHistory(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load order history');
    }
  };

  // ---- Detail view ----
  if (selected) {
    return (
      <div className="space-y-5" data-testid="customer-detail-page">
        <button
          onClick={() => { setSelected(null); setHistory(null); }}
          className="flex items-center gap-1 text-zinc-400 hover:text-gold text-sm font-sans"
          data-testid="back-to-customers"
        >
          <ChevronLeft className="w-4 h-4" /> Back to customers
        </button>

        <div className="bg-white/[0.03] border border-white/10 rounded-sm p-5">
          <h2 className="text-xl font-serif text-white">{selected.first_name} {selected.last_name}</h2>
          <div className="mt-3 space-y-1.5 text-sm font-sans text-zinc-400">
            <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {selected.email || '—'}</div>
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {selected.phone || '—'}</div>
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Joined {selected.created_at ? new Date(selected.created_at).toLocaleDateString() : '—'}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-bg-secondary border border-white/5 rounded-sm p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">Lifetime Orders</p>
              <p className="text-2xl font-serif text-gold mt-1" data-testid="customer-order-count">{selected.order_count}</p>
            </div>
            <div className="bg-bg-secondary border border-white/5 rounded-sm p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">Lifetime Spend</p>
              <p className="text-2xl font-serif text-gold mt-1" data-testid="customer-lifetime-spend">${(selected.lifetime_spend || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-sans font-bold">Order History</p>
          {!history ? (
            <p className="text-zinc-500 text-sm font-sans">Loading…</p>
          ) : history.orders.length === 0 ? (
            <p className="text-zinc-500 text-sm font-sans">No orders yet.</p>
          ) : (
            <div className="space-y-2" data-testid="customer-order-history">
              {history.orders.map(o => (
                <div key={o.id} className="bg-white/[0.03] border border-white/5 rounded-sm p-3" data-testid={`history-order-${o.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-sans text-zinc-500">#{(o.id || '').slice(0, 8)}</span>
                        <StatusPill status={o.order_status} />
                        <span className="text-[10px] uppercase tracking-wider text-zinc-600 font-sans">{(o.payment_status || 'unpaid').toUpperCase()}</span>
                      </div>
                      <p className="text-[11px] font-sans text-zinc-500 mt-0.5">
                        {o.created_at ? new Date(o.created_at).toLocaleString() : ''} · Table {o.table_number ?? '—'}
                      </p>
                    </div>
                    <p className="text-gold font-serif text-base">${Number(o.total || 0).toFixed(2)}</p>
                  </div>
                  <div className="space-y-0.5">
                    {(o.items || []).map(it => (
                      <p key={it.id} className="text-[12px] font-sans text-zinc-300">
                        {it.quantity}× {it.name}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-5" data-testid="customers-page">
      <div>
        <h1 className="text-2xl font-serif text-white">Customers</h1>
        <p className="text-sm text-zinc-500 font-sans">Search and view lifetime order history.</p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); fetchCustomers(query); }}
        className="relative max-w-md"
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or phone…"
          className="pl-9 bg-bg-secondary border-white/10 text-white font-sans"
          data-testid="customer-search-input"
        />
        <Button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 rounded-sm font-sans text-xs" data-testid="customer-search-button">
          Search
        </Button>
      </form>

      {loading ? (
        <p className="text-zinc-500 font-sans text-sm">Loading…</p>
      ) : customers.length === 0 ? (
        <p className="text-zinc-500 font-sans text-sm">No customers found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="customers-list">
          {customers.map(c => (
            <button
              key={c.id}
              onClick={() => openCustomer(c)}
              className="text-left bg-white/[0.03] border border-white/10 rounded-sm p-4 hover:border-gold/30 transition-colors"
              data-testid={`customer-card-${c.id}`}
            >
              <p className="text-white font-sans text-sm font-semibold">{c.first_name} {c.last_name}</p>
              <p className="text-zinc-500 text-xs font-sans mt-0.5 truncate">{c.email || c.phone || '—'}</p>
              <div className="flex items-center gap-3 mt-3 text-xs font-sans">
                <span className="flex items-center gap-1 text-gold"><Receipt className="w-3 h-3" /> {c.order_count}</span>
                <span className="text-zinc-400">${(c.lifetime_spend || 0).toFixed(2)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
